/** Shared sizing for the employer marks on the CPA and CMA testimonial cards.
    AboutCPA renders AboutCMA's cards alongside its own, so the two carousels
    have to agree on every value here — a local copy in either file would let
    the same logo render at two different sizes. */

/** Marks range from 5.4:1 wordmarks to a 0.95:1 square lockup. Sizing them
    all to one height makes the tall ones read as a smudge and the wide ones
    dominate, so each shape gets its own. */
export type LogoShape = "wide" | "compact" | "stacked";

/** Fields a card supplies to size its mark. */
export type LogoSizing = {
  /** Shape of the mark. Defaults to "wide"; see LOGO_HEIGHT for why. */
  logoShape?: LogoShape;
  /** Height in px, where the shape default still reads heavy or light against
      its neighbours. Width follows from the mark's own aspect ratio. */
  logoHeight?: number;
};

/** Default height per shape, picked so each carries roughly the same optical
    area: a 6.6:1 wordmark at 22px and a 1.6:1 lockup at 38px read the same
    weight. `logoHeight` overrides this where a mark still sits wrong; the
    tallest of either must clear LOGO_SLOT_CLASS, so that the rows underneath
    stay aligned across cards. */
export const LOGO_HEIGHT: Record<LogoShape, number> = {
  wide: 22,
  compact: 30,
  stacked: 38,
};

export const resolveLogoHeight = (card: LogoSizing) =>
  card.logoHeight ?? LOGO_HEIGHT[card.logoShape ?? "wide"];

/** h-11 clears the tallest mark in either set, currently Unison Globus at 42. */
export const LOGO_SLOT_CLASS = "mt-3 flex h-11 items-center justify-center";

export const LOGO_IMG_CLASS = "w-auto max-w-[140px] object-contain";

/** Employers without a mark in the asset sheet get their name set in type. */
export const isLogo = (company: string) => company.startsWith("http");

/** The logo URLs arrive with mixed query strings — some already carry a `tr`,
    some only an `updatedAt` — so strip any transform before adding ours. */
const ikTransform = (url: string, transform: string) => {
  const [base, query = ""] = url.split("?");
  const params = query
    .split("&")
    .filter((param) => param && !param.startsWith("tr="));
  params.push(`tr=${transform}`);
  return `${base}?${params.join("&")}`;
};

/** `t-true` strips the transparent canvas first — the padding runs from none
    on DXC to 39% of the height on Unison Globus, so without it the declared
    height sizes each asset's padding rather than its ink. */
const logoTransform = (url: string, height: number) =>
  ikTransform(url, `t-true,f-webp,q-90,h-${height}`);

/** Marks are 20-42px tall, and at that size a 1:1 render leaves letterforms
    visibly ragged. Starting the set at 2x means even a 1x screen downscales
    from twice the pixels, which is what actually smooths the edges — measured
    at +2.6dB against an ideal render, where raising quality or going lossless
    bought under 0.5dB for more bytes. Every mark has the source resolution to
    cover 3x without upscaling. */
const LOGO_DPRS = [2, 3];

export const logoSrc = (url: string, height: number) =>
  logoTransform(url, height * LOGO_DPRS[0]);

export const logoSrcSet = (url: string, height: number) =>
  LOGO_DPRS.map((dpr) => `${logoTransform(url, height * dpr)} ${dpr}x`).join(
    ", ",
  );
